import { useState } from "react";
import { Download, FileText, FileSpreadsheet, X } from "lucide-react";
import { API } from "../../config";

const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const getRecentMonths = (count = 12) => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = `${MONTHS_IT[d.getMonth()]} ${d.getFullYear()}`;
    out.push({ value, label });
  }
  return out;
};

export const CommunicationsExportButton = () => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(getRecentMonths(1)[0].value);
  const [channel, setChannel] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const months = getRecentMonths(12);

  const triggerDownload = (format) => {
    setDownloading(format);
    const url = `${API}/communications/export.${format}?month=${month}${format === "csv" ? `&channel=${channel}` : ""}`;
    // Force download via hidden anchor — browser handles the cookie/auth
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setDownloading(null), 1500);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600/15 border border-purple-500/30 text-purple-300 hover:bg-purple-600/25 hover:text-purple-200 transition-colors text-sm font-medium"
        data-testid="comm-export-open"
      >
        <Download size={14} />
        Export Comunicazioni
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-[#0F0F0F] border border-purple-500/30 rounded-2xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="comm-export-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Download size={18} className="text-purple-400" />
                </div>
                <h3 className="text-white text-lg font-bold">Export Comunicazioni</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white p-1" data-testid="comm-export-close">
                <X size={18} />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Estrai un report delle email e SMS inviate nel periodo selezionato. Adatto per archiviazione Compliance o rendicontazione costi.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-gray-300 text-xs uppercase tracking-wider block mb-2">Periodo</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-full bg-black border border-gray-800 rounded-lg px-3 py-2.5 text-white text-sm focus:border-purple-500 outline-none"
                  data-testid="comm-export-month"
                >
                  {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-gray-300 text-xs uppercase tracking-wider block mb-2">Canale (solo CSV)</label>
                <div className="flex gap-2">
                  {[
                    { v: "all", l: "Tutti" },
                    { v: "email", l: "Solo Email" },
                    { v: "sms", l: "Solo SMS" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => setChannel(opt.v)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${channel === opt.v ? "bg-purple-600 text-white" : "bg-black border border-gray-800 text-gray-400 hover:text-white"}`}
                      data-testid={`comm-export-channel-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-3">
                <button
                  onClick={() => triggerDownload("csv")}
                  disabled={downloading === "csv"}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-lojack-blue-600 hover:bg-lojack-blue-500 text-white font-medium transition-colors disabled:opacity-50"
                  data-testid="comm-export-csv"
                >
                  <FileSpreadsheet size={16} />
                  {downloading === "csv" ? "Scarico..." : "Scarica CSV"}
                </button>
                <button
                  onClick={() => triggerDownload("pdf")}
                  disabled={downloading === "pdf"}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50"
                  data-testid="comm-export-pdf"
                >
                  <FileText size={16} />
                  {downloading === "pdf" ? "Scarico..." : "Report PDF"}
                </button>
              </div>

              <p className="text-gray-600 text-[10px] mt-2 text-center">
                Destinatari mascherati per conformità GDPR. CSV apribile in Excel/Google Sheets.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommunicationsExportButton;
